import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";

const AudioPlayerContext = createContext(null);

// Create audio element outside React to persist across all renders
let globalAudio = null;
const getGlobalAudio = () => {
  if (!globalAudio) {
    globalAudio = new Audio();
    globalAudio.preload = "metadata";
    globalAudio.crossOrigin = "anonymous";
  }
  return globalAudio;
};

// For testing: reset the global audio singleton
export const resetGlobalAudio = () => {
  globalAudio = null;
};

const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === "AbortError") return true;
  if (
    typeof DOMException !== "undefined" &&
    typeof error.code === "number" &&
    error.code === DOMException.ABORT_ERR
  )
    return true;
  return (
    typeof error.message === "string" &&
    error.message.toLowerCase().includes("interrupted")
  );
};

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error("useAudioPlayer must be used within AudioPlayerProvider");
  }
  return context;
}

export function AudioPlayerProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tracks, setTracks] = useState([]);
  const audioRef = useRef(getGlobalAudio());
  const currentTrackRef = useRef(null);
  const tracksRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  // Helper to save track state to localStorage
  const saveTrackState = useCallback((track, time = "0") => {
    localStorage.setItem("currentTrack", JSON.stringify(track));
    localStorage.setItem("currentTime", time);
  }, []);

  // Load saved state on mount
  useEffect(() => {
    const savedTrack = localStorage.getItem("currentTrack");
    const savedTime = localStorage.getItem("currentTime");
    const savedTracks = localStorage.getItem("playlistTracks");

    if (savedTracks) {
      try {
        const parsedTracks = JSON.parse(savedTracks);
        if (Array.isArray(parsedTracks) && parsedTracks.length > 0) {
          setTracks(parsedTracks);
          tracksRef.current = parsedTracks;
        }
      } catch (err) {
        console.error("Failed to restore saved playlist:", err);
      }
    }

    if (savedTrack) {
      try {
        const track = JSON.parse(savedTrack);
        setCurrentTrack(track);
        if (audioRef.current && track.mp3_url) {
          audioRef.current.src = track.mp3_url;
          if (savedTime) {
            audioRef.current.currentTime = parseFloat(savedTime);
          }
        }
      } catch (err) {
        console.error("Failed to restore saved track:", err);
      }
    }
  }, []);

  // Save current track and time periodically
  useEffect(() => {
    if (!currentTrack || !audioRef.current) return;

    const saveState = () => {
      saveTrackState(currentTrack, audioRef.current.currentTime.toString());
    };

    // Save every 5 seconds while playing
    const interval = setInterval(() => {
      if (isPlaying) {
        saveState();
      }
    }, 5000);

    // Save on pause
    const handlePauseEvent = () => saveState();
    audioRef.current.addEventListener("pause", handlePauseEvent);

    return () => {
      clearInterval(interval);
      if (audioRef.current) {
        audioRef.current.removeEventListener("pause", handlePauseEvent);
      }
    };
  }, [currentTrack, isPlaying, saveTrackState]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      console.log("[Audio] event: play");
      setIsPlaying(true);
    };
    const handlePause = () => {
      console.log("[Audio] event: pause");
      setIsPlaying(false);
    };

    const handleLoadedMetadata = () => {
      console.log("[Audio] event: loadedmetadata", {
        duration: audio.duration,
        src: audio.src,
        readyState: audio.readyState,
      });
    };

    const handleCanPlay = () => {
      console.log("[Audio] event: canplay", {
        duration: audio.duration,
        src: audio.src,
        readyState: audio.readyState,
      });
    };

    const handleLoadStart = () => {
      console.log("[Audio] event: loadstart", {
        src: audio.src,
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
    };

    let progressCount = 0;
    const handleProgress = () => {
      progressCount += 1;
      // Throttle progress logs to reduce noise
      if (progressCount % 5 !== 1) return;

      const buffered = [];
      for (let i = 0; i < audio.buffered.length; i += 1) {
        buffered.push([audio.buffered.start(i), audio.buffered.end(i)]);
      }
      console.log("[Audio] event: progress", {
        readyState: audio.readyState,
        networkState: audio.networkState,
        buffered,
        duration: audio.duration,
      });
    };

    const handleStalled = () => {
      console.warn("[Audio] event: stalled", {
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
    };

    const handleSuspend = () => {
      console.log("[Audio] event: suspend", {
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
    };

    const handleWaiting = () => {
      console.warn("[Audio] event: waiting", {
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
    };

    const handleAbort = () => {
      console.warn("[Audio] event: abort", {
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
    };

    const handleError = () => {
      const mediaError = audio.error;
      console.error("[Audio] event: error", {
        code: mediaError?.code,
        message: mediaError?.message,
        src: audio.src,
        networkState: audio.networkState,
        readyState: audio.readyState,
      });
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("progress", handleProgress);
    audio.addEventListener("stalled", handleStalled);
    audio.addEventListener("suspend", handleSuspend);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("abort", handleAbort);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("loadstart", handleLoadStart);
      audio.removeEventListener("progress", handleProgress);
      audio.removeEventListener("stalled", handleStalled);
      audio.removeEventListener("suspend", handleSuspend);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("abort", handleAbort);
      audio.removeEventListener("error", handleError);
    };
  }, []);

  const playTrack = useCallback(
    (track) => {
      const audio = audioRef.current;
      if (!audio || !track?.mp3_url) return;

      // Reset and load new source
      console.log("[Audio] playTrack called", track);
      audio.pause();
      audio.src = track.mp3_url;
      audio.currentTime = 0;

      // Optimistically update UI state so selection/highlight updates immediately
      setCurrentTrack(track);
      setIsPlaying(true);
      saveTrackState(track, "0");

      console.log("[Audio] calling play()", {
        src: audio.src,
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
      const playPromise = audio.play();
      if (!playPromise) {
        return;
      }

      playPromise
        .then(() => {
          // Playback started successfully; state already set above
          console.log("[Audio] play() promise resolved", {
            src: audio.src,
            readyState: audio.readyState,
          });
        })
        .catch((error) => {
          if (isAbortError(error)) {
            // Retry once after an abort to kick off the new source
            const retry = audio.play();
            console.warn("[Audio] play() aborted, retrying once", {
              src: audio.src,
              readyState: audio.readyState,
            });
            if (retry?.catch) {
              retry.catch((retryError) => {
                if (!isAbortError(retryError)) {
                  console.error("[Audio] Retry playback failed:", retryError);
                }
              });
            }
            return;
          }
          console.error("Playback failed:", error);
          setIsPlaying(false);
        });
    },
    [saveTrackState]
  );

  // Auto-play next track when current ends
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      const playlist = tracksRef.current || [];
      if (playlist.length === 0) {
        setIsPlaying(false);
        return;
      }

      const currentIndex = playlist.findIndex(
        (t) => t.mp3_url === currentTrackRef.current?.mp3_url
      );
      const nextIndex = currentIndex + 1;

      if (nextIndex < playlist.length) {
        const nextTrack = playlist[nextIndex];
        playTrack(nextTrack);
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, [playTrack]);

  const loadPlaylist = useCallback((playlistTracks, initialTrack) => {
    setTracks(playlistTracks);
    tracksRef.current = playlistTracks;
    try {
      localStorage.setItem("playlistTracks", JSON.stringify(playlistTracks));
    } catch (err) {
      console.error("Failed to persist playlistTracks:", err);
    }

    // If audio is already playing, don't interrupt it
    if (audioRef.current && audioRef.current.src && !audioRef.current.paused) {
      console.log("[Audio] loadPlaylist skipped because already playing");
      return;
    }

    // Check if there's a saved track that matches the playlist
    const savedTrack = localStorage.getItem("currentTrack");
    const savedTime = localStorage.getItem("currentTime");

    if (savedTrack) {
      try {
        const track = JSON.parse(savedTrack);
        const matchingTrack = playlistTracks.find(
          (t) => t.mp3_url === track.mp3_url
        );

        if (matchingTrack && audioRef.current) {
          audioRef.current.src = matchingTrack.mp3_url;
          setCurrentTrack(matchingTrack);
          if (savedTime) {
            audioRef.current.currentTime = parseFloat(savedTime);
          }
          return;
        }
      } catch (err) {
        console.error("Failed to restore saved track:", err);
      }
    }

    // No saved track or doesn't match - use initial track
    if (initialTrack && audioRef.current) {
      console.log("[Audio] loadPlaylist setting initial track", initialTrack);
      audioRef.current.src = initialTrack.mp3_url;
      setCurrentTrack(initialTrack);
    }
  }, []);

  const stopAndClear = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setCurrentTrack(null);
    setIsPlaying(false);
    setTracks([]);
    tracksRef.current = [];
    localStorage.removeItem("currentTrack");
    localStorage.removeItem("currentTime");
    localStorage.removeItem("playlistTracks");
  }, []);

  const nextTrack = useCallback(() => {
    if (tracks.length === 0 || !currentTrack) return;

    const currentIndex = tracks.findIndex(
      (t) => t.mp3_url === currentTrack.mp3_url
    );
    const nextIndex = currentIndex + 1;

    if (nextIndex < tracks.length) {
      playTrack(tracks[nextIndex]);
    }
  }, [tracks, currentTrack, playTrack]);

  const previousTrack = useCallback(() => {
    if (tracks.length === 0 || !currentTrack) return;

    const currentIndex = tracks.findIndex(
      (t) => t.mp3_url === currentTrack.mp3_url
    );
    const prevIndex = currentIndex - 1;

    if (prevIndex >= 0) {
      playTrack(tracks[prevIndex]);
    }
  }, [tracks, currentTrack, playTrack]);

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        playTrack,
        loadPlaylist,
        stopAndClear,
        audioRef,
        nextTrack,
        previousTrack,
        tracks,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}
